import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, 
  CreditCard, 
  Mail, 
  BarChart2, 
  DollarSign,
  ArrowUp,
  ArrowDown,
  Loader
} from 'lucide-react';
import { dbPromise } from '../lib/db';
import WaveAnimation from '../components/WaveAnimation';

interface DashboardStats {
  totalUsers: number;
  totalRevenue: number;
  totalMessages: number;
  totalPayments: number;
  recentPayments: Array<{
    id: number;
    name: string;
    amount: number;
    status: string;
    createdAt: string;
  }>;
  recentMessages: Array<{
    id: number;
    name: string;
    subject: string;
    createdAt: string;
  }>;
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<DashboardStats>({
    totalUsers: 0,
    totalRevenue: 0,
    totalMessages: 0,
    totalPayments: 0,
    recentPayments: [],
    recentMessages: []
  });

  useEffect(() => {
    const checkAdmin = () => {
      const user = localStorage.getItem('user');
      if (!user) {
        navigate('/login');
        return false;
      }
      
      const userData = JSON.parse(user);
      if (userData.email !== 'notrakibs@gmail.com') {
        navigate('/');
        return false;
      }
      
      return true;
    };

    const loadStats = async () => {
      if (!checkAdmin()) return;

      try {
        const db = await dbPromise;
        
        // Get users count
        const usersTx = db.transaction('users', 'readonly');
        const users = await usersTx.store.getAll();
        
        // Get messages
        const messagesTx = db.transaction('contact_messages', 'readonly');
        const messages = await messagesTx.store.getAll();
        
        // Get payments
        const paymentsTx = db.transaction('payment_requests', 'readonly');
        const payments = await paymentsTx.store.getAll();
        
        // Calculate total revenue from approved payments
        const revenue = payments
          .filter(p => p.status === 'approved')
          .reduce((sum, p) => sum + p.amount, 0);

        // Sort and get recent items
        const recentPayments = [...payments]
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
          .slice(0, 5);

        const recentMessages = [...messages]
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
          .slice(0, 5);

        setStats({
          totalUsers: users.length,
          totalRevenue: revenue,
          totalMessages: messages.length,
          totalPayments: payments.length,
          recentPayments,
          recentMessages
        });
      } catch (error) {
        console.error('Failed to load dashboard stats:', error);
      } finally {
        setLoading(false);
      }
    };

    loadStats();
  }, [navigate]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 pt-24 flex items-center justify-center">
        <Loader className="w-8 h-8 text-purple-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 pt-24 relative overflow-hidden">
      <WaveAnimation />
      
      <div className="container mx-auto px-4 py-12 relative z-10">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl font-bold text-white mb-8">Admin Dashboard</h1>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-gray-800/50 backdrop-blur-sm rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 mb-1">Total Users</p>
                  <h3 className="text-2xl font-bold text-white">{stats.totalUsers}</h3>
                </div>
                <div className="bg-purple-500/20 p-3 rounded-lg">
                  <Users className="w-6 h-6 text-purple-400" />
                </div>
              </div>
              <div className="flex items-center gap-1 mt-4 text-green-400">
                <ArrowUp className="w-4 h-4" />
                <span className="text-sm">12% from last month</span>
              </div>
            </div>

            <div className="bg-gray-800/50 backdrop-blur-sm rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 mb-1">Total Revenue</p>
                  <h3 className="text-2xl font-bold text-white">
                    BDT {stats.totalRevenue.toLocaleString()}
                  </h3>
                </div>
                <div className="bg-green-500/20 p-3 rounded-lg">
                  <DollarSign className="w-6 h-6 text-green-400" />
                </div>
              </div>
              <div className="flex items-center gap-1 mt-4 text-green-400">
                <ArrowUp className="w-4 h-4" />
                <span className="text-sm">8% from last month</span>
              </div>
            </div>

            <div className="bg-gray-800/50 backdrop-blur-sm rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 mb-1">Total Messages</p>
                  <h3 className="text-2xl font-bold text-white">{stats.totalMessages}</h3>
                </div>
                <div className="bg-blue-500/20 p-3 rounded-lg">
                  <Mail className="w-6 h-6 text-blue-400" />
                </div>
              </div>
              <div className="flex items-center gap-1 mt-4 text-red-400">
                <ArrowDown className="w-4 h-4" />
                <span className="text-sm">3% from last month</span>
              </div>
            </div>

            <div className="bg-gray-800/50 backdrop-blur-sm rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 mb-1">Total Payments</p>
                  <h3 className="text-2xl font-bold text-white">{stats.totalPayments}</h3>
                </div>
                <div className="bg-yellow-500/20 p-3 rounded-lg">
                  <CreditCard className="w-6 h-6 text-yellow-400" />
                </div>
              </div>
              <div className="flex items-center gap-1 mt-4 text-green-400">
                <ArrowUp className="w-4 h-4" />
                <span className="text-sm">15% from last month</span>
              </div>
            </div>
          </div>

          {/* Recent Activity */}
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Recent Payments */}
            <div className="bg-gray-800/50 backdrop-blur-sm rounded-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-white">Recent Payments</h2>
                <button 
                  onClick={() => navigate('/admin/payments')}
                  className="text-purple-400 hover:text-purple-300 transition-colors"
                >
                  View all
                </button>
              </div>
              
              <div className="space-y-4">
                {stats.recentPayments.map(payment => (
                  <div key={payment.id} className="flex items-center justify-between">
                    <div>
                      <p className="text-white font-medium">{payment.name}</p>
                      <p className="text-gray-400 text-sm">
                        BDT {payment.amount.toLocaleString()}
                      </p>
                    </div>
                    <div className="text-right">
                      <span className={`inline-block px-2 py-1 rounded text-xs ${
                        payment.status === 'approved' 
                          ? 'bg-green-500/20 text-green-300'
                          : payment.status === 'rejected'
                          ? 'bg-red-500/20 text-red-300'
                          : 'bg-yellow-500/20 text-yellow-300'
                      }`}>
                        {payment.status}
                      </span>
                      <p className="text-gray-400 text-sm mt-1">
                        {formatDate(payment.createdAt)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Recent Messages */}
            <div className="bg-gray-800/50 backdrop-blur-sm rounded-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-white">Recent Messages</h2>
                <button 
                  onClick={() => navigate('/admin/messages')}
                  className="text-purple-400 hover:text-purple-300 transition-colors"
                >
                  View all
                </button>
              </div>
              
              <div className="space-y-4">
                {stats.recentMessages.map(message => (
                  <div key={message.id} className="flex items-center justify-between">
                    <div>
                      <p className="text-white font-medium">{message.name}</p>
                      <p className="text-gray-400 text-sm">{message.subject}</p>
                    </div>
                    <div className="text-gray-400 text-sm">
                      {formatDate(message.createdAt)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}